// src/components/ArtworkCard.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, Typography, Button, Stack, Dialog, Box } from '@mui/material';
import { BuyButton } from '../components/BuyButton';
import { http } from "../lib/axios";
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import ArtworkEditModal from './ArtworkEditModal';
import { EngagementSummary } from "../components/EngagementSummary";
import CollapsibleComments from "../components/CollapsibleComments";

let cachedMe = null;
let cachedMePromise = null;
let cachedMeLoaded = false;

async function getCurrentUserOnce() {
  if (cachedMeLoaded) return cachedMe;
  if (!cachedMePromise) {
    cachedMePromise = http.get('/api/accounts/me/', { ignore401: true })
      .then((res) => {
        cachedMe = res?.data || null;
        cachedMeLoaded = true;
        return cachedMe;
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          cachedMe = null;
          cachedMeLoaded = true;
          return null;
        }
        throw err;
      })
      .finally(() => {
        cachedMePromise = null;
      });
  }
  return cachedMePromise;
}

// Normalize any absolute media URL to a relative one so CRA proxy handles it.
function toRelativeMedia(url) {
  if (!url) return url;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (url.startsWith('/media/')) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url.replace(/^https?:\/\/[^/]+/, '');
  }
  const trimmed = url.replace(/^\.?\/*/, '');
  return trimmed.startsWith('media/') ? `/${trimmed}` : `/media/${trimmed}`;
}

export default function ArtworkCard({
  artwork,
  art,
  userRole,
  onDelete,
  onMarkSold,
  allowMarkSold = false,
  showActions = true,
  customActionButton,
  // NEW: allow host (gallery) to manage represented artworks
  canHostManage = false,
  canManageOverride = null,
  cardSx = {},
}) {
  const data = useMemo(() => artwork || art || {}, [artwork, art]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isDescriptionOverflowing, setIsDescriptionOverflowing] = useState(false);
  const descriptionRef = useRef(null);

  useEffect(() => {
    const fetchUserId = async () => {
      try {
        const me = await getCurrentUserOnce();
        setCurrentUserId(me?.id ?? null);
        setCurrentUserRole(me?.role ?? null);
      } catch (err) {
        console.error('Failed to fetch current user', err);
      }
    };
    fetchUserId();
  }, []);

  // Images
  const artworkImages = useMemo(() => {
    const imgs = [];
    if (Array.isArray(data.images) && data.images.length) {
      data.images.forEach((img) => {
        const src = img?.url || img?.image || img?.image_url;
        if (src) imgs.push({ src: toRelativeMedia(src), caption: data.title });
      });
    }
    const fallback = data.primary_image_url || data.image || data.image_url;
    if (imgs.length === 0 && fallback) {
      imgs.push({ src: toRelativeMedia(fallback), caption: data.title });
    }
    const seen = new Set();
    return imgs.filter(({ src }) => (src && !seen.has(src) && seen.add(src)));
  }, [data]);

  const sliderSettings = {
    dots: true,
    infinite: artworkImages.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    autoplay: artworkImages.length > 1,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    pauseOnFocus: true,
    arrows: true,
  };

  const handleSendRequest = async (type) => {
    const isBuy = type === 'buy';
    const endpoint = isBuy ? `request_buy` : `request_see`;
    const artworkLabel = formatValue(data.title);
    const hostLabel = hostName !== '-' ? hostName : 'the host';

    try {
      await http.post(`/artworks/${data.id}/${endpoint}/`, {});
      if (isBuy) {
        window.alert(`Your request to buy artwork "${artworkLabel}" has been sent.`);
        return;
      }
      window.alert(`Your request to see artwork "${artworkLabel}" has been sent to host "${hostLabel}".`);
    } catch (_err) {
      window.alert('Failed to send request');
    }
  };

  const handleDeleteArtwork = () => {
    if (!window.confirm('Are you sure you want to delete this artwork? This action cannot be undone.')) return;

    http.delete(`/artworks/${data.id}/`)
      .then(() => {
        alert('Artwork deleted successfully');
        if (onDelete) onDelete(data.id);
      })
      .catch(err => {
        console.error('Delete failed', err);
        alert('Failed to delete artwork');
      });
  };

  const handleImageClick = (index) => {
    setCurrentImageIndex(index);
    setPreviewOpen(true);
  };

  const isOwnerArtist =
    currentUserRole === 'artist' &&
    data.artist?.user?.id === currentUserId;

  // NEW: single flag for whether the current user can manage this artwork
  const canManage =
    typeof canManageOverride === 'boolean'
      ? canManageOverride
      : (isOwnerArtist || canHostManage);

  const isBuyer = currentUserRole === 'buyer';
  const isSold = data.status === 'sold';
  const canDirectBuyerPurchase = !(
    data?.representation_mode === 'gallery_represented' &&
    !data?.gallery_representation_active
  );
  const formatValue = (value) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'string' && value.trim() === '') return '-';
    return value;
  };
  const descriptionText = formatValue(data.description);
  const priceValue = formatValue(data.price);
  const displayPrice = priceValue === '-' ? '-' : `$${priceValue}`;
  const pickString = (...values) => values.find((value) => typeof value === 'string' && value.trim()) || '';
  const artistName = data.artist
    ? (
      data.artist.display_name ||
      [data.artist.user?.first_name, data.artist.user?.last_name].filter(Boolean).join(' ').trim() ||
      formatValue(data.artist.name ?? data.artist)
    )
    : '-';
  const hostProfileId =
    (data.host && typeof data.host === 'object' && data.host.id) ||
    data.host_id ||
    (typeof data.host === 'number' || typeof data.host === 'string' ? data.host : null);
  const hostData = (data.host && typeof data.host === 'object' ? data.host : {});
  const hostUserData = (hostData.user && typeof hostData.user === 'object') ? hostData.user : {};
  const hostFullName = [
    hostUserData.first_name,
    hostUserData.last_name,
    hostData.first_name,
    hostData.last_name,
  ]
    .filter(Boolean)
    .join(' ')
    .trim();
  const hostName = hostFullName ||
    pickString(
      hostData.display_name,
      hostData.name,
      hostData.venue_name,
      hostData.display_name,
      hostUserData.full_name,
      hostUserData.name,
      hostUserData.username,
      data.host_name,
      data.host_display_name,
    ) ||
    '-';

  useEffect(() => {
    setShowFullDescription(false);
  }, [descriptionText, data?.id]);

  useEffect(() => {
    if (!descriptionRef.current || showFullDescription) return;

    const el = descriptionRef.current;
    const hasOverflow = el.scrollHeight > el.clientHeight + 1;

    setIsDescriptionOverflowing(hasOverflow);
  }, [descriptionText, showFullDescription]);

  const renderSubcategories = () => {
    const entries = (data.subcategories && typeof data.subcategories === 'object')
      ? Object.entries(data.subcategories)
      : [];

    if (entries.length === 0) {
      return (
        <>
          <Typography variant="body2" color="text.secondary">
            Subcategories: -
          </Typography>
          <Typography variant="body2" color="text.secondary">&nbsp;</Typography>
          <Typography variant="body2" color="text.secondary">&nbsp;</Typography>
        </>
      );
    }

    const rendered = entries.map(([key, value]) => (
      <Typography key={key} variant="body2" color="text.secondary">
        {key}: {formatValue(value)}
      </Typography>
    ));

    while (rendered.length < 3) {
      rendered.push(
        <Typography key={`subcat-pad-${rendered.length}`} variant="body2" color="text.secondary">
          &nbsp;
        </Typography>
      );
    }

    return rendered;
  };

  const hasData = data && typeof data === 'object' && Object.keys(data).length > 0;
  if (!hasData) return null;

  const renderImages = () => {
    if (artworkImages.length === 0) {
      return (
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              height: 250,
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textTransform: 'uppercase',
              letterSpacing: 2,
              fontWeight: 700,
              color: 'text.secondary',
              bgcolor: 'action.hover'
            }}
          >
            NO IMAGE
          </Box>
        </Box>
      );
    }

    if (artworkImages.length === 1) {
      const img = artworkImages[0];
      return (
        <Box sx={{ p: 2 }}>
          <Box
            sx={{ height: 250, position: 'relative', overflow: 'hidden', cursor: 'zoom-in' }}
            onClick={() => handleImageClick(0)}
          >
            {/* Blurred background */}
            <Box
              component="img"
              src={img.src}
              alt=""
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'blur(20px)',
                transform: 'scale(1.1)',
                opacity: 0.3
              }}
            />
            {/* Main contained image */}
            <Box
              component="img"
              src={img.src}
              alt={img.caption || data.title || 'Artwork'}
              sx={{
                position: 'relative',
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                display: 'block',
                margin: '0 auto',
                height: '100%'
              }}
            />
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        <Slider {...sliderSettings}>
          {artworkImages.map((img, idx) => (
            <Box
              key={idx}
              sx={{ height: 300, position: 'relative', overflow: 'hidden', cursor: 'zoom-in' }}
              onClick={() => handleImageClick(idx)}
            >
              {/* Blurred background */}
              <Box
                component="img"
                src={img.src}
                alt=""
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  filter: 'blur(20px)',
                  transform: 'scale(1.1)',
                  opacity: 0.3
                }}
              />
              {/* Main contained image */}
              <Box
                component="img"
                src={img.src}
                alt={img.caption || 'Artwork'}
                sx={{
                  position: 'relative',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  display: 'block',
                  margin: '0 auto',
                  height: '100%'
                }}
              />
            </Box>
          ))}
        </Slider>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: 360,
        flex: '0 0 auto', // prevents shrinking in sliders
        mx: 'auto',
        height: '100%'
      }}
    >
      <Card sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%', 
        minHeight: 620, 
        backgroundColor: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(16px)',
        ...cardSx,
      }}>

        {renderImages()}
        <CardContent
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography variant="h6">{formatValue(data.title)}</Typography>
            <Box sx={{ position: 'relative', mt: 0.5 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                ref={descriptionRef}
                onClick={() => {
                  if (!showFullDescription && isDescriptionOverflowing) {
                    setShowFullDescription(true);
                  }
                }}
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: showFullDescription ? 'unset' : 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  cursor: !showFullDescription && isDescriptionOverflowing ? 'pointer' : 'default',
                  minHeight: '2.8em',
                  lineHeight: 1.4,
                  whiteSpace: 'pre-line',
                }}
              >
                {descriptionText}
              </Typography>

              {!showFullDescription && isDescriptionOverflowing && (
                <Box
                  role="button"
                  aria-label="Show full description"
                  onClick={() => setShowFullDescription(true)}
                  sx={{
                    position: 'absolute',
                    right: 0,
                    bottom: 0,
                    px: 0.5,
                    pb: 0.1,
                    background: (theme) => `linear-gradient(90deg, transparent, ${theme.palette.background.paper})`,
                    color: 'primary.main',
                    cursor: 'pointer',
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  ...
                </Box>
              )}
            </Box>
            {showFullDescription && isDescriptionOverflowing && (
              <Button
                variant="text"
                size="small"
                onClick={() => setShowFullDescription(false)}
                sx={{
                  alignSelf: 'flex-start',
                  textTransform: 'none',
                  minWidth: 0,
                  px: 0,
                  mt: 0.5
                }}
              >
                Show less
              </Button>
            )}
              <Typography variant="subtitle2" mt={1}>Price: {displayPrice}</Typography>

            {data?.id && (
              <div style={{ marginTop: 12 }}>
                <EngagementSummary
                  target="artwork"
                  objectId={data.id}
                  initialSummary={data.engagement_summary}
                  trailingAction={
                    isBuyer && !isOwnerArtist && !isSold ? (
                      canDirectBuyerPurchase ? (
                      <BuyButton
                        artworkId={data.id}
                        hostId={data.host?.id || undefined}
                        iconOnly
                      />
                      ) : null
                    ) : null
                  }
                />
              </div>
            )}

            <Typography variant="body2">
              Artist:{' '}
              {artistName !== '-' && data.artist ? (
                <Link
                  to={`/artists/${data.artist.id}`}
                  style={{ color: "#2563eb", textDecoration: "none" }}
                >
                  {artistName}
                </Link>
              ) : (
                <>{artistName}</>
              )}
            </Typography>
            {(data.status === 'hosted' || hostProfileId) && (
              <Typography variant="body2">
                Host:{' '}
                {hostName !== '-' && hostProfileId ? (
                  <Link
                    to={`/hosts/${hostProfileId}`}
                    style={{ color: "#2563eb", textDecoration: "none" }}
                  >
                    {hostName}
                  </Link>
                ) : (
                  <>{hostName}</>
                )}
              </Typography>
            )}
            <Typography variant="body2">
              Category: {formatValue(data.artwork_type)}
            </Typography>
            {renderSubcategories()}
            <Typography variant="body2">Dimensions: {formatValue(data.dimensions)}</Typography>
            <Typography variant="caption" color="primary">
              {formatValue(data.status)}
            </Typography>
            {data?.representation_mode === 'gallery_represented' && (
              <Typography variant="caption" color={data?.gallery_representation_active ? "success.main" : "warning.main"} sx={{ display: 'block' }}>
                {data?.gallery_representation_active ? 'Gallery representation active' : 'Awaiting gallery claim'}
              </Typography>
            )}
          </Box>

          <Stack spacing={1} mt={2} sx={{ mt: 'auto' }}>
            {/* Request to See if hosted and user is buyer */}
            {data.status === 'hosted' && currentUserRole === 'buyer' && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => handleSendRequest('see')}
              >
                Request to See
              </Button>
            )}

            {showActions && (
              <>
                {/* NEW: owner artist OR gallery host representing this artwork */}
                {canManage && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setEditOpen(true)}
                  >
                    Edit
                  </Button>
                )}

                {canManage && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={handleDeleteArtwork}
                  >
                    Delete Artwork
                  </Button>
                )}

                {onMarkSold && allowMarkSold && canManage && data.status !== 'sold' && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="success"
                    onClick={() => onMarkSold(data.id)}
                  >
                    Mark as Sold
                  </Button>
                )}

                <Button
                  component={Link}
                  to={`/artists/${data.artist?.id ?? data.artist}`}
                  variant="outlined"
                  size="small"
                  color="secondary"
                >
                  View Artist
                </Button>

                {customActionButton && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={customActionButton.onClick}
                    disabled={customActionButton.disabled}
                  >
                    {customActionButton.label}
                  </Button>
                )}
              </>
            )}
          </Stack>

          {data?.id && (
            <CollapsibleComments target="artwork" objectId={data.id} />
          )}
        </CardContent>
      </Card>

      {/* Fullscreen preview modal */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth={false}>
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
        >
          <Box sx={{ width: { xs: '95vw', md: '80vw' }, maxWidth: 1200 }}>
            <Slider dots arrows infinite initialSlide={currentImageIndex} adaptiveHeight>
              {artworkImages.map((img, i) => (
                <Box
                  key={i}
                  sx={{ height: { xs: '70vh', md: '80vh' }, position: 'relative', overflow: 'hidden' }}
                >
                  {/* blurred backdrop */}
                  <Box
                    component="img"
                    src={img.src}
                    alt=""
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      filter: 'blur(22px)',
                      transform: 'scale(1.1)',
                      opacity: 0.25
                    }}
                  />
                  {/* contained main image */}
                  <Box
                    component="img"
                    src={img.src}
                    alt={img.caption || 'Artwork'}
                    sx={{
                      position: 'relative',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      display: 'block',
                      m: '0 auto',
                      height: '100%'
                    }}
                  />
                  {img.caption && (
                    <Typography
                      variant="body2"
                      align="center"
                      sx={{
                        position: 'absolute',
                        bottom: 12,
                        left: 0,
                        right: 0,
                        color: '#fff',
                        bgcolor: 'rgba(0,0,0,0.35)',
                        px: 1,
                        py: 0.5
                      }}
                    >
                      {img.caption}
                    </Typography>
                  )}
                </Box>
              ))}
            </Slider>
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button variant="outlined" onClick={() => setPreviewOpen(false)}>
                Close
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>

      <ArtworkEditModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        artwork={data}
        onUpdated={(updated) => {
          console.log("Artwork updated:", updated);
        }}
      />
    </Box>
  );
}
